"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { type NavigateFunction, useNavigate } from "react-router-dom"
import Backdrop from "components/Backdrop"
import ErrorBox from "components/ErrorBox"
import { toast } from "react-toastify"
import { usePermissions } from "app/hooks"
import { getEmployeeSubordinates, batchCreateEmployeeUtilizations,getWorkCategories } from "api/apiHelper"
import { EmployeeUtilizationBatchCreateType, Employee, EmployeeUtilizationBatchType, UtilizationRecord, WorkCategory } from "types/employee-utilization"

const CreateEmployeeUtilization = () => {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [subordinates, setSubordinates] = useState<Employee[] | null>(null)
  const [workCategories, setWorkCategories] = useState<WorkCategory[]>([])
  
  const [utilizationData, setUtilizationData] = useState<EmployeeUtilizationBatchCreateType>({
    date: new Date().toISOString().split("T")[0],
    utilizations: [],
  })
  const navigate: NavigateFunction = useNavigate()
  const permissions = usePermissions() || []
  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(new Set())

  // Add category state
  const [employeeCategories, setEmployeeCategories] = useState<Record<number, string>>({})

  async function fetchSubordinates() {
    try {
      const subordinateData = await getEmployeeSubordinates()
      console.log("Subordinates: ", subordinateData)

      setSubordinates(subordinateData)
      setUtilizationData((prev) => ({
        ...prev,
        utilizations: subordinateData.map((emp) => ({
          employee: Number(emp.id),
          utilization_hours: 8,
        })),
      }))

      // Select all employees by default
      setSelectedEmployees(new Set(subordinateData.map((emp) => Number(emp.id))))

      // Initialize categories with default "development"
      const initialCategories: Record<number, string> = {}
      subordinateData.forEach((emp) => {
        initialCategories[Number(emp.id)] = ""
      })
      setEmployeeCategories(initialCategories)

      setIsLoading(false)
    } catch (error: any) {
      console.error(error)
      setError(error.response?.data?.detail || error.message)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSubordinates()
  }, [])
  // Fetch work categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await getWorkCategories();
        setWorkCategories(categories);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };
  
    fetchCategories();
  }, []);

  const handleUtilizationChange = (employeeId: number, value: string) => {
    const newValue = Math.min(12, Math.max(1, Number.parseFloat(value) || 0))

    setUtilizationData((prev) => ({
      ...prev,
      utilizations: prev.utilizations.map((util) =>
        util.employee === employeeId ? { ...util, utilization_hours: newValue } : util,
      ),
    }))
  }

  const handleEmployeeSelection = (employeeId: number) => {
    const newSelected = new Set(selectedEmployees)
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId)
    } else {
      newSelected.add(employeeId)
    }
    setSelectedEmployees(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedEmployees.size === subordinates?.length) {
      setSelectedEmployees(new Set())
    } else {
      setSelectedEmployees(new Set(subordinates?.map((emp) => Number(emp.id)) || []))
    }
  }

  const getSelectedTotalHours = () => {
    return utilizationData.utilizations
      .filter((util) => selectedEmployees.has(util.employee))
      .reduce((total, util) => total + util.utilization_hours, 0)
  }

  const getSelectedAverageHours = () => {
    const selectedCount = selectedEmployees.size
    return selectedCount > 0 ? (getSelectedTotalHours() / selectedCount).toFixed(1) : "0.0"
  }

  const handleCategoryChange = (employeeId: number, category: string) => {
    setEmployeeCategories((prev) => ({
      ...prev,
      [employeeId]: category,
    }))
  }

  const setBulkCategory = (category: string) => {
    const updates: Record<number, string> = {}
    selectedEmployees.forEach((employeeId) => {
      updates[employeeId] = category
    })
    setEmployeeCategories((prev) => ({
      ...prev,
      ...updates,
    }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
  
    if (selectedEmployees.size === 0) {
      toast.error("Please select at least one employee to create records for.");
      return;
    }
  
    // Validate that all selected employees have categories selected
    const employeesWithoutCategory = Array.from(selectedEmployees).filter(
      employeeId => !employeeCategories[employeeId]
    );
    
    if (employeesWithoutCategory.length > 0) {
      toast.error("Please select work category for all selected employees.");
      return;
    }
  
    const notification = toast.loading("Creating utilization records...");
    setIsLoading(true);
    setError("");
  
    try {
      // Transform the data to match EmployeeUtilizationCreateSerializer format
      const records = utilizationData.utilizations
        .filter((util) => selectedEmployees.has(util.employee))
        .map((util) => ({
          date: utilizationData.date,
          employee: util.employee,
          items: [
            {
              type: Number(employeeCategories[util.employee]),
              utilization_hours: util.utilization_hours
            }
          ]
        }));
  
      console.log("Submitting records:", records);
  
      const response = await batchCreateEmployeeUtilizations(records);
      console.log("Response:", response.data);
  
      toast.update(notification, {
        render: `Created utilization records for ${selectedEmployees.size} employees`,
        type: "success",
        isLoading: false,
        autoClose: 5000,
      })
      navigate("/employee-utilization/employees/")
    } catch (error: any) {
      console.error("Full error object:", error)
      console.error("Error response:", error.response)
      console.error("Error data:", error.response?.data)
      
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data) || error.message
      setError(errorMessage)
      
      toast.update(notification, {
        render: "Failed to create utilization records",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // const getTotalHours = () => {
  //   return utilizationData.utilizations.reduce((total, util) => total + util.utilization_hours, 0)
  // }

  // const getAverageHours = () => {
  //   const total = getTotalHours()
  //   return subordinates ? (total / subordinates.length).toFixed(1) : 0
  // }

  const setAllHours = (hours: number) => {
    setUtilizationData((prev) => ({
      ...prev,
      utilizations: prev.utilizations.map((util) => ({
        ...util,
        utilization_hours: hours,
      })),
    }))
  }

  if (isLoading) {
    return <Backdrop show={isLoading} />
  }

  if (!permissions.includes("employee_utilization.add_employeeutilization")) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center p-8 bg-white dark:bg-navy-800 rounded-2xl shadow-xl max-w-md">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-lock text-3xl text-red-400"></i>
          </div>
          <h3 className="text-xl font-semibold text-navy-700 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-600 dark:text-gray-300">You don't have permission to create utilization records.</p>
        </div>
      </div>
    )
  }

  if (!subordinates || subordinates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center p-8 bg-white dark:bg-navy-800 rounded-2xl shadow-xl max-w-md">
          <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-users text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-xl font-semibold text-navy-700 dark:text-white mb-2">No Team Members</h3>
          <p className="text-gray-600 dark:text-gray-300">You don't have any subordinates to manage utilization for.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto py-6 space-y-6">
      {error && (
        <div className="mb-6">
          <ErrorBox errorMessage={error} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Selected Hours</p>
                <p className="text-3xl font-bold text-navy-700 dark:text-white">{getSelectedTotalHours()}</p>
              </div>
              <div className="w-12 h-12 bg-brand-50 dark:bg-brand-900/20 rounded-xl flex items-center justify-center">
                <i className="fas fa-clock text-xl text-brand-600 dark:text-brand-400"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Selected Employees</p>
                <p className="text-3xl font-bold text-navy-700 dark:text-white">{selectedEmployees.size}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                <i className="fas fa-users text-xl text-purple-600 dark:text-purple-400"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Date</p>
                <p className="text-lg font-semibold text-navy-700 dark:text-white">
                  {new Date(utilizationData.date).toLocaleDateString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center">
                <i className="fas fa-calendar text-xl text-emerald-600 dark:text-emerald-400"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Date Selection & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Date Selection */}
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
                <i className="fas fa-calendar-alt text-brand-600 dark:text-brand-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Select Date</h3>
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Utilization Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="date"
                  value={utilizationData.date}
                  onChange={(e) => setUtilizationData({ ...utilizationData, date: e.target.value })}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
                <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <i className="fas fa-magic text-purple-600 dark:text-purple-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Quick Set</h3>
            </div>

            {/* Hours Quick Set */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Set Hours for All
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setAllHours(8)}
                  className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-navy-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-all group"
                >
                  <i className="fas fa-business-time text-lg text-gray-600 dark:text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">8 Hours</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAllHours(6)}
                  className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-navy-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-all group"
                >
                  <i className="fas fa-clock text-lg text-gray-600 dark:text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">6 Hours</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAllHours(4)}
                  className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-navy-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-all group"
                >
                  <i className="fas fa-hourglass-half text-lg text-gray-600 dark:text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">4 Hours</span>
                </button>
              </div>
            </div>

            {/* Category Quick Set */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Set Category for Selected ({selectedEmployees.size})
              </label>
              <select
                value=""
                onChange={(e) => e.target.value && setBulkCategory(e.target.value)}
                disabled={selectedEmployees.size === 0}
                className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm disabled:opacity-50"
              >
                <option value="">Choose category...</option>
                {(workCategories || []).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Employee Utilization Grid */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
                  <i className="fas fa-users text-brand-600 dark:text-brand-400"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy-700 dark:text-white">Team Utilization</h3>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={selectedEmployees.size === subordinates?.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 dark:focus:ring-brand-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="selectAll" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All ({selectedEmployees.size}/{subordinates?.length})
                  </label>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-navy-700 dark:text-white">{subordinates.length}</div>
                    <div className="text-gray-500 dark:text-gray-400">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-navy-700 dark:text-white">{getSelectedAverageHours()}</div>
                    <div className="text-gray-500 dark:text-gray-400">Avg Hours</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {subordinates.map((employee, index) => {
                const currentHours =
                  utilizationData.utilizations.find((u) => u.employee === Number(employee.id))?.utilization_hours || 8
                const isHighUtilization = currentHours >= 9
                const isFullyUtilized = currentHours === 8
                const isLowUtilization = currentHours <= 5

                return (
                  <div
                    key={employee.id}
                    className={`relative bg-gray-50 dark:bg-navy-700 rounded-lg p-4 border transition-all hover:shadow-md cursor-pointer ${
                      selectedEmployees.has(Number(employee.id))
                        ? "ring-2 ring-brand-500 border-brand-300 dark:border-brand-600"
                        : isHighUtilization
                          ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10"
                          : isFullyUtilized
                            ? "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10"
                            : isLowUtilization
                              ? "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10"
                              : "border-gray-200 dark:border-navy-600"
                    }`}
                    onClick={() => handleEmployeeSelection(Number(employee.id))}
                  >
                    {/* Selection Checkbox */}
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.has(Number(employee.id))}
                        onChange={() => handleEmployeeSelection(Number(employee.id))}
                        className="w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 dark:focus:ring-brand-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* Employee Info */}
                    <div className="flex items-center gap-3 mb-3 pr-8">
                      <div className="relative">
                        <div className="w-10 h-10 bg-brand-500 dark:bg-brand-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {employee.first_name[0]}
                          {employee.last_name[0]}
                        </div>
                        <div
                          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-navy-700 ${
                            isHighUtilization
                              ? "bg-red-400"
                              : isFullyUtilized
                                ? "bg-green-400"
                                : isLowUtilization
                                  ? "bg-orange-400"
                                  : "bg-green-400"
                          }`}
                        ></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-navy-700 dark:text-white truncate text-sm">
                          {employee.first_name} {employee.last_name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {isHighUtilization
                            ? "Overburdened"
                            : isFullyUtilized
                              ? "Fully Utilized"
                              : isLowUtilization
                                ? "Low Utilization"
                                : "Standard Hours"}
                        </p>
                      </div>
                      <span
                        className={`text-lg font-bold ${
                          isHighUtilization ? "text-red-600" : isLowUtilization ? "text-orange-600" : "text-brand-600"
                        }`}
                      >
                        {currentHours}h
                      </span>
                    </div>

                    {/* Hours Input */}
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={currentHours}
                          onChange={(e) => handleUtilizationChange(Number(employee.id), e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-navy-600 rounded-md bg-white dark:bg-navy-800 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                        />
                        <i className="fas fa-clock absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                      </div>

                      {/* Quick Hour Buttons */}
                      <div className="flex gap-1">
                        {[4, 6, 8].map((hours) => (
                          <button
                            key={hours}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUtilizationChange(Number(employee.id), hours.toString())
                            }}
                            className={`flex-1 py-1 px-2 text-xs font-medium rounded transition-all ${
                              currentHours === hours
                                ? "bg-brand-500 text-white"
                                : "bg-gray-200 dark:bg-navy-600 text-gray-600 dark:text-gray-300 hover:bg-brand-100 dark:hover:bg-brand-900/30"
                            }`}
                          >
                            {hours}h
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Work Category */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Work Category
                      </label>
                      <div className="relative">
                        <select
                          value={employeeCategories[Number(employee.id)] || ""}
                          onChange={(e) => handleCategoryChange(Number(employee.id), e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-navy-600 rounded-md bg-white dark:bg-navy-800 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm appearance-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="" disabled>Select any</option>
                          {(workCategories || []).map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.type}</option>
                          ))}
                        </select>
                        <i className="fas fa-briefcase absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                        <i className="fas fa-chevron-down absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate("/employee-utilization/employees")}
            className="px-6 py-2.5  bg-gray-700 hover:bg-red-500 text-white rounded-lg font-medium transition-all"
          >
            <i className="fas fa-times mr-2"></i>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all disabled:cursor-not-allowed"
          >
            <i className={`${isLoading ? "fas fa-spinner fa-spin" : "fas fa-save"} mr-2`}></i>
            {isLoading ? "Creating..." : `Create Records (${selectedEmployees.size})`}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CreateEmployeeUtilization
